import { html, nothing, PropertyValueMap, TemplateResult, CSSResultGroup } from 'lit';
import { property, query, state } from 'lit/decorators.js';
import { DirectiveResult } from 'lit/directive';
import { ClassMapDirective } from 'lit/directives/class-map';
import { ifDefined } from 'lit/directives/if-defined.js';
import { nanoid } from 'nanoid';
import { SlotController } from '../../controllers/slot-controller';
import { BlocksChangeEvent } from '../../events/blocks-change';
import type { CommonAriaLiveValue } from '../../util/common-modal';
import { BlocksElement } from '../BlocksElement';
import '../field-note/field-note';
import '../icon/icons/warning-diamond-filled';
import '../icon/icons/warning-triangle-filled';
import '../skeleton/skeleton';
import type { TextareaFieldSize, TextAreaFieldStatus, TextareaResizeVariant } from './textarea-field.model';
import styles from './textarea-field.styles';

/**
 * @slot error - error slot
 * @slot warning - warning slot
 * @slot field-note - default field-note
 * @slot word-count - custom word count slot
 * @slot custom-count - Can displays custom count/words
 * @slot after - The component after content
 *
 * @fires bl-change - Emitted when value change
 *
 * @csspart base - The component's base wrapper.
 * @csspart label-container - The component's label-container for loader.
 * @csspart md-label-loader - The component's md-label-loader wrapper.
 * @csspart md-label-skeleton - The component's md-label-skeleton wrapper.
 * @csspart max-length-loader - The component's max-length-loader.
 * @csspart max-length-skeleton - The component's max-length-skeleton element.
 * @csspart loader - The component's loader wrapper.
 * @csspart skeleton-loader - The component's skeleton-loader element.
 * @csspart field-note-loader - The component's field-note-loader wrapper.
 * @csspart field-note-skeleton - The component's field-note-skeleton element.
 * @csspart header - The component's header wrapper.
 * @csspart label-wrap - The component's label wrapper.
 * @csspart label-inner-wrapper - the component's label inner wrapper
 * @csspart part-label - The component's label element.
 * @csspart part-notation - The component's notation element.
 * @csspart part-word-count - The component's word-count slot.
 * @csspart part-custom-count - The component's custom-count slot
 * @csspart max-length - The component's max-length wrapper.
 * @csspart container - The component's container.
 * @csspart body - The component's body.
 * @csspart wrapper - The component's wrapper.
 * @csspart status-icon - The component's status-icon element.
 * @csspart part-after - The component's after slot.
 * @csspart input - The component's `<textarea>` element.
 * @csspart footer - The component's footer wrapper.
 * @csspart field-note-wrap - The component's field-note wrapper.
 * @csspart part-field-note - The component's field-note slot.
 * @csspart part-warning - The component's warning slot.
 * @csspart part-error - The component's error slot.
 */
export class BlocksTextareaField extends BlocksElement {
  static formAssociated = true;static styles: CSSResultGroup = styles;

  /**
   * The unique id of the field
   */
  @property({ type: String })
  fieldId: string | undefined;

  /**
   * The form field's label
   */
  @property({ type: String })
  label = 'Label';

  /**
   * Placeholder attribute
   */
  @property({ type: String })
  placeholder: string | undefined;

  /**
   * Checkbox name attribute property
   */
  @property({ type: String })
  name: string | undefined;

  /**
   * Value
   */
  @property({ type: String })
  value: string | undefined;

  /**
   * Rows
   * 1) Specifies the visible number of lines in a textarea
   */
  @property({ type: Number })
  rows = 3;

  /**
   * Columns
   * 1) Specifies the visible width of a textarea
   */
  @property({ type: Number })
  cols: number | undefined;

  /**
   * Fieldnote text
   */
  @property({ type: String })
  fieldNote: string | undefined;

  /**
   * Errornote text
   */
  @property({ type: String })
  errorNote: string | undefined;

  /**
   * Warningnote text
   */
  @property({ type: String })
  warningNote: string | undefined;

  /**
   * Notation is used for setting required or optional label
   */
  @property({ type: String })
  notation: string | undefined;

  /**
   * Aria describedby
   * 1) Used to connect the field note in select field to the select menu for accessibility
   */
  @property({ type: String })
  ariaDescribedBy: string | undefined;

  /**
   * Required attribute
   */
  @property({ type: Boolean })
  required = false;

  /**
   * Disabled attribute
   */
  @property({ type: Boolean, reflect: true })
  disabled: boolean | undefined;

  /**
   * AutoFocus attribute
   */
  @property({ type: Boolean, reflect: true })
  autoFocus: boolean | undefined;

  /**
   * Hide label?
   */
  @property({ type: Boolean })
  hideLabel: boolean | undefined;

  /**
   * Maxlength of characters for the textarea field
   */
  @property({ type: Number })
  maxlength: number | undefined;

  /**
   * A boolean property to specify whether the word count should be hidden or displayed.
   */
  @property({ type: Boolean }) hideWordCount = false;

  /**
   * Readonly attribute
   */
  @property({ type: Boolean, reflect: true })
  readonly: boolean | undefined;

  /**
   * Loading state
   * 1) Adds a loader if is true
   */
  @property({ type: Boolean })
  isLoading: boolean | undefined;

  /**
   * Status of input field
   */
  @property({ type: String })
  status: TextAreaFieldStatus;

  /**
   * Resize option for autoresize the text area
   */
  /**
   * Resize variant
   * <cat-text-passage size="sm">
   * <ul>
   * <li> **none** disables resizing of the textarea</li>
   * <li> **auto** resizes to the height of the content entered into the textarea</li>
   * </ul>
   * </cat-text-passage>
   */
  @property({ type: String })
  resize: TextareaResizeVariant;

  /**
   * Size
   */
  @property({ type: String })
  size: TextareaFieldSize = 'lg';

  /**
   * Defines a template for localizing character count messages for screen reader announcements.
   */
  @property({ type: String })
  charCountTemplate: string | undefined;

  /**
   * The ARIA live region politeness level for the fieldNote. To be used
   * to prioritize the announcement of helper Text
   *
   * @type {string}
   * @default "polite"
   */
  @property({ type: String })
  arialive: CommonAriaLiveValue = 'polite';

  /**
   * Name or Title for the warning icon
   * meant for accessibility purposes only
   */
  @property({ type: String })
  warningIconTitle = 'warning icon';

  /**
   * Name or Title for the error icon
   * meant for accessibility purposes only
   */
  @property({ type: String })
  errorIconTitle = 'error icon';

  private internals_ = this.attachInternals?.();
  private maxLengthId = nanoid();

  /**
   * Associates the control with a form element
   */
  get form(): HTMLFormElement | null {
    return this.internals_?.form;
  }

  @state() private isFocus = false;
  @state() private isActive = false;

  /**
   * state for aria-describedby of text area field
   */
  @state()
  private descriptionIds: string | undefined;

  /**
   * To keep track of not typing state
   */
  @state() private isNotTyping = true;

  @query('textarea') private textareaEl!: HTMLInputElement;

  protected slots = new SlotController(this, 'error', 'warning', 'field-note', 'word-count', 'after', 'custom-count');

  private get hasFieldNoteSlot(): boolean {
    return this.slots.hasSlotted('field-note');
  }

  private get hasErrorSlot(): boolean {
    return this.slots.hasSlotted('error');
  }

  private get hasWarningSlot(): boolean {
    return this.slots.hasSlotted('warning');
  }

  /**
   * Connected callback lifecycle
   * 1) Autogenerate ids, for attribute, and aria-describedby ids to make this accessible if these properties aren't provided
   * 2) On resize of the textarea, set the input height
   */
  connectedCallback() {
    super.connectedCallback();
    /* 1 */
    this.fieldId = this.fieldId || nanoid();

    /* 2 */
    window.addEventListener('resize', this.setInputHeight);
  }

  protected updated(_changedProperties: PropertyValueMap<any> | Map<PropertyKey, unknown>): void {
    if (_changedProperties.has('value') && this.value !== undefined) {
      this.internals_?.setFormValue(this.value);
      this.setInputHeight();
    }

    if (_changedProperties.has('autoFocus') && this.autoFocus) {
      this.focus();
    }
  }

  override focus(): void {
    this.textareaEl?.focus();
  }

  /**
   * 1) Gathering all ids which can be assigned to aria-describedby
   */
  private setAriaDescribedBy() {
    let listOfIds = '';

    /* 1 */
    if (!this.hideWordCount && this.maxlength) {
      listOfIds = `${this.maxLengthId}`;
    }
    if (this.hasFieldNote()) {
      this.ariaDescribedBy = this.ariaDescribedBy || nanoid();
      listOfIds = `${this.ariaDescribedBy} ${listOfIds}`;
    }
    const trimmedIds = listOfIds.trim();
    this.descriptionIds = trimmedIds.length > 0 ? trimmedIds : undefined;
  }

  /**
   * Disconnected callback lifecycle
   */
  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('resize', this.setInputHeight);
  }

  /**
   * Change output binding
   * @param $event focused element
   */
  private handleOnChanged = async (e: Event) => {
    const value = (e.target as HTMLTextAreaElement).value;
    this.value = value;
    await this.updateComplete;
    this.handleOnFocus(e);
    this.dispatch({ eventName: 'bl-change', detailObj: { value } }) as BlocksChangeEvent;
  };

  /**
   * 1.) This will not focus on clicking in readonly mode
   */
  private handleOnClick() {
    if (this.readonly) {
      /* 1 */
      this.handleOnFocusOut();
    }
  }

  private handleOnFocus(e: Event) {
    const textInput = e.target as HTMLTextAreaElement;
    const value = textInput.value;

    this.isNotTyping = e.type === 'focus';
    if (value) {
      this.isActive = true;
    } else {
      this.isActive = false;
      this.isFocus = true;
    }
  }

  private handleOnFocusOut() {
    this.isActive = false;
    this.isFocus = false;
    this.isNotTyping = true;
  }

  private hasFieldNote() {
    return this.fieldNote || this.errorNote || this.warningNote || this.hasFieldNoteSlot || this.hasErrorSlot || this.hasWarningSlot;
  }

  /**
   * Set input height based on the content
   */
  private setInputHeight = () => {
    if (this.resize === 'auto') {
      const input = this.shadowRoot?.querySelector<HTMLTextAreaElement>('.cat-c-textarea-field__input')!;
      const inputPadding = window.getComputedStyle(input, null).getPropertyValue('padding-block');
      input.rows = this.rows;
      const inputHeight = input.scrollHeight - 2 * parseInt(inputPadding);
      const lineHeight = window.getComputedStyle(input, null).getPropertyValue('line-height');
      const rowsCount = inputHeight / parseInt(lineHeight);
      if (rowsCount > this.rows) {
        input.rows = rowsCount;
      }
    }
  };

   /**
   * For displaying label
   */
  private getLabel() {
    return html` <span part="header" class="cat-c-textarea-field__header">
      <label
        part="label-wrap"
        class="cat-c-textarea-field__label"
        for="${ifDefined(this.fieldId)}"
        aria-hidden=${ifDefined(this.isNotTyping ? false : true)}
      >
        <span part="label-inner-wrapper" class="cat-c-textarea-field__label-inner-wrapper">
          <span part="part-label" class="cat-c-textarea-field--label">${this.label}</span>
          <span part="part-notation" class="cat-c-textarea-field__notation-text">${this.notation}</span>
        </span>
      </label>
      
      ${!this.hideWordCount && this.maxlength && this.maxlength > 0
            ? html`<slot part="part-word-count" name="word-count">
              <span part="max-length" class="cat-c-text-field__maxlength" id=${ifDefined(this.maxLengthId)}>
                ${this.value && this.value.length > 0 ? this.value.length : 0}/${this.maxlength}
              </span>
            </slot>`
            : html`<slot part="part-custom-count" name="custom-count" class="cat-c-text-field__customCount">`}
    </span>`;
  }

  private renderLoading(componentClassNames: DirectiveResult<typeof ClassMapDirective>) {
    return html`
      <div part="base" class="${componentClassNames}">
        ${(this.size === 'md' || this.size === 'lg') && !this.hideLabel
          ? html`
              <div part="label-container" class="cat-c-text-area__label-container">
                <span part="md-label-loader" class="cat-c-text-area__md-label-loader">
                  <cat-skeleton
                    part="md-label-skeleton"
                    exportparts=${this.getExportParts('skeleton-label', 'skeleton')}
                    class="cat-c-text-area__md-label-loader-skeleton"
                    size="sm"
                  ></cat-skeleton>
                </span>
                ${this.slots.hasSlotted('word-count') || (!this.hideWordCount && this.maxlength)
                  ? html`<span part="max-length-loader" class="cat-c-text-area__max-length-loader">
                      <cat-skeleton
                        part="max-length-skeleton"
                        exportparts=${this.getExportParts('skeleton-max-length', 'skeleton')}
                        class="cat-c-text-area__max-length-loader-skeleton"
                        size="sm"
                      ></cat-skeleton>
                    </span>`
                  : nothing}
              </div>
            `
          : nothing}
        <div part="loader" class="cat-c-text-area__input-loader">
          <cat-skeleton
            part="skeleton-loader"
            exportparts=${this.getExportParts('skeleton-loader', 'skeleton')}
            class="cat-c-text-area__input-loader"
            style="${' --cat-skeleton-loader-height:' + 20 * this.rows + 'px'}"
          >
          </cat-skeleton>
        </div>
        ${this.hasFieldNote()
          ? html`<div part="field-note-loader" class="cat-c-text-area__fieldnote-loader">
              <cat-skeleton
                part="field-note-skeleton"
                exportparts=${this.getExportParts('skeleton-field-note', 'skeleton')}
                class="cat-c-text-area__fieldnote-loader-skeleton"
                size="sm"
              ></cat-skeleton>
            </div>`
          : nothing}
      </div>
    `;
  }

  render(): TemplateResult<1> {
    const componentClassNames = this.componentClassNames('cat-c-textarea-field', {
      'cat-is-disabled': this.disabled,
      'cat-is-readonly': this.readonly === true,
      'cat-c-text-area--loader': this.isLoading,
      'cat-c-textarea-field--hidden-label': this.hideLabel,
      'cat-is-error': this.status === 'error',
      'cat-is-warning': this.status === 'warning',
      'cat-has-icon--after': this.slots.hasSlotted('after'),
      'cat-c-textarea-field--no-resize': this.resize === 'none',
      'cat-c-textarea-field--auto': this.resize === 'auto',
      'cat-c-textarea-field--medium': this.size === 'md',
      'cat-c-textarea-field--large': this.size === 'lg',
      'cat-c-textarea-field--has-word-count': !this.hideWordCount && this.maxlength
    });
    this.setAriaDescribedBy();

    const bodyClassNames = this.componentClassNames('cat-c-textarea-field__body', {
      'cat-textarea-body__active': this.isActive === true,
      'cat-textarea-body__focus': this.isFocus === true
    });

    if (this.isLoading) {
      return this.renderLoading(componentClassNames);
    }

    return html`
      <div part="base" class="${componentClassNames}">
        ${(this.size === 'md' || this.size === 'lg') && !this.hideLabel ? this.getLabel() : nothing}

        <span part="container" class="cat-c-text-area--container">
          <div part="body" class="${bodyClassNames}">
            <div part="wrapper" class="cat-c-textarea-wrapper">
              <textarea
                part="input"
                class="cat-c-textarea-field__input"
                id="${ifDefined(this.fieldId)}"
                name="${ifDefined(this.name)}"
                .value="${this.value ?? ''}"
                rows="${ifDefined(this.rows)}"
                cols="${ifDefined(this.cols)}"
                ?disabled="${this.disabled}"
                ?readonly="${this.readonly}"
                ?autofocus="${this.autoFocus}"
                aria-describedby="${ifDefined(this.isNotTyping ? this.descriptionIds : undefined)}"
                aria-required="${ifDefined(!this.notation ? (this.required ? 'true' : undefined) : undefined)}"
                placeholder="${ifDefined(this.placeholder)}"
                maxlength="${ifDefined(this.maxlength)}"
                @change=${(e: Event) => this.handleOnChanged(e)}
                @input=${(e: Event) => this.handleOnChanged(e)}
                @focus=${(e: Event) => this.handleOnFocus(e)}
                @blur=${() => this.handleOnFocusOut()}
                @click=${() => this.handleOnClick()}
              ></textarea>
              ${this.status === 'error'
                ? html` <cat-icon-warning-diamond-filled
                    part="status-icon"
                    exportparts=${this.getExportParts('warning-diamond-filled', 'icon')}
                    color="error"
                    .iconTitle=${this.errorIconTitle}
                    class="cat-c-text-area__error-icon"
                  ></cat-icon-warning-diamond-filled>`
                : nothing}
              ${this.status === 'warning'
                ? html` <cat-icon-warning-triangle-filled
                    part="status-icon"
                    exportparts=${this.getExportParts('warning-triangle-filled', 'icon')}
                    color="knockout-brand"
                    .iconTitle=${this.warningIconTitle}
                    class="cat-c-text-area__warning-icon"
                  ></cat-icon-warning-triangle-filled>`
                : nothing}
              <slot part="slot-after" name="after" class="cat-icon"></slot>
            </div>
          </div>
        </span>

        <div part="footer" class=${this.hasFieldNote() ? 'cat-c-textarea-field__footer' : 'cat-u-is-vishidden'} aria-live=${this.arialive}>
          <cat-field-note
            part="field-note-wrap"
            exportparts=${this.getExportParts('field-note')}
            id=${ifDefined(this.fieldNote || this.hasFieldNoteSlot ? this.ariaDescribedBy : undefined)}
            ?isDisabled=${this.disabled}
            .status=${this.status}
            class="cat-c-textarea-field__field-note"
          >
            <slot part="part-field-note" name="field-note">${this.fieldNote}</slot>
          </cat-field-note>

          <cat-field-note
            part="field-note-wrap"
            exportparts=${this.getExportParts('field-note')}
            id=${ifDefined(this.warningNote || this.hasWarningSlot ? this.ariaDescribedBy : undefined)}
            ?isDisabled=${this.disabled}
            class="cat-c-textarea-field__field-note"
          >
            <slot part="part-warning" name="warning">${this.warningNote}</slot>
          </cat-field-note>

          <cat-field-note
            part="field-note-wrap"
            exportparts=${this.getExportParts('field-note')}
            id=${ifDefined(this.errorNote || this.hasErrorSlot ? this.ariaDescribedBy : undefined)}
            ?isDisabled=${this.disabled}
            .status=${'error'}
            class="cat-c-textarea-field__field-note"
          >
            <slot part="part-error" name="error">${this.errorNote}</slot>
          </cat-field-note>

          <div class="cat-u-is-vishidden">
            ${this.charCountTemplate
              ? this.charCountTemplate
              : this.maxlength && this.value
                ? `${this.maxlength - this.value?.length} of ${this.maxlength} characters remaining`
                : html`${nothing}`}
          </div>
        </div>
      </div>
    `;
  }
}

if (customElements.get('cat-textarea-field') === undefined) {
  customElements.define('cat-textarea-field', BlocksTextareaField);
}

declare global {
  interface HTMLElementTagNameMap {
    'cat-textarea-field': BlocksTextareaField;
  }
}
